import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Collapse, Typography, makeStyles } from '@material-ui/core'
import type { SpecIntelligenceFinding } from 'api/graphQL/types/specIntelligence.types'
import clsx from 'clsx'
import React, { useMemo } from 'react'
import FindingCardActions from './components/FindingCardActions'
import FindingCardHeader from './components/FindingCardHeader'
import GoToSectionLink from './components/GoToSectionLink'
import { renderDescriptionWithParagraphRefs, stripParagraphRefs } from './descriptionUtils'

const useStyles = makeStyles(() => ({
    card: {
        borderBottom: '1px solid #EEEEEE',
        padding: `${pxToRem(12)} ${pxToRem(12)} ${pxToRem(8)}`,
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#FFF' },
    },
    cardSelected: {
        backgroundColor: '#FFF',
    },
    cardReadOnly: {
        cursor: 'default',
    },
    description: {
        fontSize: pxToRem(13),
        color: '#424242',
        lineHeight: 1.5,
    },
    descriptionLine: {
        display: 'block',
    },
    referencedSection: {
        color: '#212121',
        fontWeight: 500,
    },
}))

interface ReadOnlyFindingCardProps {
    /** The finding to display. */
    finding: SpecIntelligenceFinding
    /**
     * Renders the card without expand/collapse or Resolve/Dismiss actions, showing a
     * "Go to section" link instead (when the finding has a `sectionElementId`). Requires
     * `projectId` to build that link.
     */
    readOnly: true
    /** The current project id, used to build the "Go to section" link. */
    projectId: string
}

interface InteractiveFindingCardProps {
    /** The finding to display. */
    finding: SpecIntelligenceFinding
    readOnly?: false
    /** Called when the user clicks Resolve. */
    onResolve: (id: string) => void
    /** Called when the user clicks Dismiss. */
    onDismiss: (id: string) => void
    /** Whether this card is the currently expanded/selected one. */
    expanded: boolean
    /** Called when the card header is clicked to toggle expansion. */
    onToggle: () => void
}

/** Props for {@link SpecIntelligenceFindingCard}. */
export type SpecIntelligenceFindingCardProps = ReadOnlyFindingCardProps | InteractiveFindingCardProps

/**
 * Collapsed/expanded card for a single SpecIntelligence finding. Expansion is controlled
 * by the parent so only one card in a list can be expanded at a time.
 */
const SpecIntelligenceFindingCard = (props: SpecIntelligenceFindingCardProps) => {
    const classes = useStyles()
    const { finding } = props
    const descriptionNodes = useMemo(
        () => renderDescriptionWithParagraphRefs(finding.description, classes.descriptionLine, classes.referencedSection),
        [finding.description, classes.descriptionLine, classes.referencedSection]
    )

    if (props.readOnly) {
        const { projectId } = props
        return (
            <div className={clsx(classes.card, classes.cardReadOnly)}>
                <FindingCardHeader finding={finding} expanded={false} readOnly />
                <Typography className={classes.description}>
                    {descriptionNodes}
                </Typography>
                {finding.sectionElementId && (
                    <GoToSectionLink projectId={projectId} sectionElementId={finding.sectionElementId} />
                )}
            </div>
        )
    }

    const { onResolve, onDismiss, expanded, onToggle } = props
    const cleanDescription = stripParagraphRefs(finding.description)

    return (
        <div
            className={clsx(classes.card, expanded && classes.cardSelected)}
            onClick={onToggle}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            aria-label={`${finding.sectionNumber} · ${finding.sectionName}: ${cleanDescription}`}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onToggle()
                }
            }}
        >
            <FindingCardHeader finding={finding} expanded={expanded} />
            <Typography className={classes.description}>
                {descriptionNodes}
            </Typography>
            <Collapse in={expanded} unmountOnExit>
                <FindingCardActions findingId={finding.id} onResolve={onResolve} onDismiss={onDismiss} />
            </Collapse>
        </div>
    )
}

export default SpecIntelligenceFindingCard
